// Shared loan-report engine — "Monthly report" card embedded by the
// Insurance and UCC tabs. Generate report (prefilled with the current
// calendar month) → preview drawer → Certify & file → ledger refresh.
// Generation is gated on Loans.Manage (hidden when lacking; the server
// enforces authoritatively).

import { useState } from "react";
import { Button, Card } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { useAuth } from "@/hooks/useAuth";
import { extractErrorMessage } from "@/api/client";
import type { LoanReportDto } from "@/api/portfolioAdmin/loanReportsApi";
import type { LoanSummaryDto } from "@/api/portfolioAdmin/types";
import {
  useGenerateLoanReport,
  useLoanReports,
} from "@/hooks/portfolioAdmin/useLoanReports";
import { LoanReportLedger, disciplineLabel } from "./LoanReportLedger";
import { LoanReportPreviewDrawer } from "./LoanReportPreviewDrawer";

function currentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

/** "2026-06" → { periodStart: "2026-06-01", periodEnd: "2026-06-30" }. */
export function monthRange(month: string): {
  periodStart: string;
  periodEnd: string;
} {
  const [y, m] = month.split("-").map(Number);
  const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return {
    periodStart: `${month}-01`,
    periodEnd: `${month}-${String(lastDay).padStart(2, "0")}`,
  };
}

interface LoanReportsCardProps {
  loan: LoanSummaryDto;
  discipline: "Insurance" | "Ucc";
}

export function LoanReportsCard({ loan, discipline }: LoanReportsCardProps) {
  const auth = useAuth();
  const canManage = auth.hasPermission("Loans.Manage");
  const toast = useToast();

  const reportsQuery = useLoanReports(loan.id, discipline);
  const generate = useGenerateLoanReport(loan.id, discipline);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [month, setMonth] = useState(currentMonth);
  const [openReportId, setOpenReportId] = useState<string | null>(null);
  // The freshly generated report may not be in the cached list yet — keep
  // it as a fallback so the drawer can open immediately on success.
  const [lastGenerated, setLastGenerated] = useState<LoanReportDto | null>(null);

  const reports = reportsQuery.data;
  const openReport = openReportId
    ? reports?.find((r) => r.id === openReportId) ??
      (lastGenerated?.id === openReportId ? lastGenerated : null)
    : null;

  const openConfirm = () => {
    setMonth(currentMonth());
    setConfirmOpen(true);
  };

  const onGenerate = async () => {
    if (!month) {
      toast.error("Choose a report period.");
      return;
    }
    try {
      const report = await generate.mutateAsync(monthRange(month));
      setLastGenerated(report);
      setConfirmOpen(false);
      setOpenReportId(report.id);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  return (
    <Card data-testid={`loan-reports-card-${discipline.toLowerCase()}`}>
      <div className="flex items-center justify-between gap-space-3 flex-wrap mb-space-3">
        <h3 className="font-display font-bold text-h3 m-0 min-w-0 truncate">
          Monthly report
        </h3>
        {canManage && (
          <Button
            variant="primary"
            size="sm"
            className="shrink-0"
            onClick={openConfirm}
            data-testid="loan-report-generate"
          >
            Generate report
          </Button>
        )}
      </div>

      <LoanReportLedger
        loanId={loan.id}
        reports={reports}
        isLoading={reportsQuery.isLoading}
        isError={reportsQuery.isError}
        onRetry={() => void reportsQuery.refetch()}
        onRowClick={(r) => setOpenReportId(r.id)}
        onGenerate={canManage ? openConfirm : undefined}
      />

      <Modal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title={`Generate ${disciplineLabel(discipline)} monthly report`}
        description="The report covers one calendar month."
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => void onGenerate()}
              loading={generate.isPending}
              data-testid="loan-report-generate-confirm"
            >
              Generate
            </Button>
          </>
        }
      >
        <label className="flex flex-col gap-space-1 text-caption">
          <span className="text-gray-mid uppercase tracking-label">
            Report period
          </span>
          <input
            type="month"
            className="h-9 px-space-2 border border-border-light rounded-md text-small bg-white-pure"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            aria-label="Report period"
            data-testid="loan-report-month"
          />
        </label>
      </Modal>

      {openReport && (
        <LoanReportPreviewDrawer
          loanId={loan.id}
          report={openReport}
          open={!!openReport}
          onClose={() => setOpenReportId(null)}
        />
      )}
    </Card>
  );
}
